import { Fragment, FC } from 'react'
// i18n
import { useTranslation } from '@/hooks/useTranslation'
// components
import { Listbox, Transition } from '@headlessui/react'
import WithdrawSpeed from '@/components/Withdraw/src/Speed'
// constants
import { SPEED } from '@/components/Withdraw/constants'
import { isEthChain } from '@/components/Withdraw/src/constants'
// types
import { ChainSelectorProps } from './types'

export const ChainSelector: FC<ChainSelectorProps> = ({
  chains,
  selectedChain,
  onChainChange,
  disabled = false,
  className = '',
  walletType = 'mpc',
}) => {
  const { t } = useTranslation()

  return (
    <div className={`relative ${className}`}>
      <Listbox
        value={selectedChain}
        onChange={onChainChange}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Listbox.Button
              className={`
                relative w-full h-9 text-text-primary bg-fill-input-primary
                items-center justify-between flex px-3 rounded-lg
                focus:outline-none 
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-fill-btn-secondary-hover'}
                ${open ? 'ring-1 ring-white' : ''}
              `}
            >
              <span className="text-sm leading-[18px] font-medium">
                {t('deposit.chain')}
              </span>

              <div className="flex items-center space-x-2">
                {/* Chain Icon */}
                {selectedChain.chainIconUrl ? (
                  <img
                    src={selectedChain.chainIconUrl}
                    alt={selectedChain.chain}
                    className="w-4 h-4 rounded-full"
                  />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-xs text-white font-bold">
                      {selectedChain.chain.charAt(0)}
                    </span>
                  </div>
                )}

                {/* Chain Name */}
                <span className="text-sm text-white ml-1">
                  {selectedChain.chain}
                </span>

                {/* Dropdown Arrow */}
                <svg
                  className={`w-3 h-3 ml-2 transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="11"
                  viewBox="0 0 10 11"
                  fill="none"
                >
                  <g clipPath="url(#clip0_12919_27745)">
                    <path
                      d="M5.32733 7.6716C5.13944 7.83432 4.86056 7.83432 4.67267 7.6716L1.68366 5.08304C1.33371 4.77997 1.54804 4.20508 2.01099 4.20508L7.98901 4.20508C8.45196 4.20508 8.66629 4.77998 8.31634 5.08304L5.32733 7.6716Z"
                      fill="#F9F9F9"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_12919_27745">
                      <rect
                        width="10"
                        height="10"
                        fill="white"
                        transform="translate(0 0.955078)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </Listbox.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Listbox.Options
                className={`
                  absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg
                  bg-fill-input-primary border border-line-divider-secondary shadow-lg
                  focus:outline-none
                `}
              >
                {chains
                  .filter(chain => {
                    // 根据钱包类型选择对应的字段
                    const allowDepositField =
                      walletType === 'mpc' ? 'allowAaDeposit' : 'allowDeposit'
                    return (chain as any)[allowDepositField] !== false
                  })
                  .map(chain => (
                    <Listbox.Option
                      key={chain.chainId}
                      value={chain}
                      className={({ active, selected }) =>
                        `relative cursor-pointer select-none py-3 px-3 ${
                          selected
                            ? 'bg-fill-btn-secondary'
                            : active
                              ? 'bg-fill-input-secondary'
                              : ''
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {/* Chain Icon */}
                              {chain.chainIconUrl ? (
                                <img
                                  src={chain.chainIconUrl}
                                  alt={chain.chain}
                                  className="w-5 h-5 rounded-full"
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                                  <span className="text-xs text-white font-bold">
                                    {chain.chain.charAt(0)}
                                  </span>
                                </div>
                              )}

                              {/* Chain Info */}
                              <div className="flex flex-col">
                                <span
                                  className={`text-sm font-medium ${
                                    selected || active
                                      ? 'text-text-primary'
                                      : 'text-text-tertiary'
                                  }`}
                                >
                                  {chain.chain}
                                </span>
                              </div>
                            </div>
                            <div className="w-12 bg-fill-page-secondary p-1 h-5 flex rounded">
                              <WithdrawSpeed
                                className="w-full justify-between"
                                selectedLevel={
                                  isEthChain(chain.chainId)
                                    ? SPEED.SLOW
                                    : SPEED.FAST
                                }
                                maxLevel={3}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </Listbox.Option>
                  ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  )
}
